import React, { useEffect, useMemo, useState } from "react";

import moment, { Moment } from "moment";

import { useTranslation, useValidateCropPeriods } from "utils";

import { CropFCPO, CropFormValidationError } from "models";

import { AlertBanner, Flex, Form, RangePicker } from "components";

export type SelectedCropWithFieldId = { crop: CropFCPO; fieldId: string };

type DateRange = [Moment | null, Moment | null];

type MissingCropGrowingPeriodProps = {
  recommendedSeedingDate: Moment;
  recommendedHarvestDate: Moment;
  existingCropsForValidationByField: Record<string, CropFCPO[]>;
  selectedCropsWithFieldIds: SelectedCropWithFieldId[];
  disabled: boolean;
};

type InitialDateState = {
  hasMixedDates: boolean;
  initialSeedingRange: DateRange;
  initialHarvestRange: DateRange;
};

const EMPTY_DATE_RANGE: DateRange = [null, null];

function toMomentRange(start: string | null | undefined, end: string | null | undefined): DateRange {
  return [start ? moment(start) : null, end ? moment(end) : null];
}

function computeInitialDateRanges(crops: CropFCPO[]): InitialDateState {
  const emptyResult = (hasMixedDates: boolean): InitialDateState => ({
    hasMixedDates,
    initialSeedingRange: EMPTY_DATE_RANGE,
    initialHarvestRange: EMPTY_DATE_RANGE,
  });

  if (crops.length <= 1) {
    const c = crops[0];
    // only pre-fill when the crop has actual seeding and harvest dates (not fallback-only)
    if (!c?.seedingStartDate && !c?.harvestEndDate) return emptyResult(false);
    return {
      hasMixedDates: false,
      initialSeedingRange: toMomentRange(c.seedingStartDate, c.seedingEndDate),
      initialHarvestRange: toMomentRange(c.harvestStartDate, c.harvestEndDate),
    };
  }

  const ref = crops[0];
  const allSameSeedingStart = crops.every((c) => (c.seedingStartDate ?? "") === (ref.seedingStartDate ?? ""));
  const allSameSeedingEnd = crops.every((c) => (c.seedingEndDate ?? "") === (ref.seedingEndDate ?? ""));
  const allSameHarvestStart = crops.every((c) => (c.harvestStartDate ?? "") === (ref.harvestStartDate ?? ""));
  const allSameHarvestEnd = crops.every((c) => (c.harvestEndDate ?? "") === (ref.harvestEndDate ?? ""));
  const hasSameDates =
    allSameSeedingStart &&
    allSameSeedingEnd &&
    allSameHarvestStart &&
    allSameHarvestEnd &&
    ref.seedingStartDate &&
    ref.seedingEndDate &&
    ref.harvestStartDate &&
    ref.harvestEndDate;

  if (!hasSameDates) return emptyResult(true);

  return {
    hasMixedDates: false,
    initialSeedingRange: toMomentRange(ref.seedingStartDate, ref.seedingEndDate),
    initialHarvestRange: toMomentRange(ref.harvestStartDate, ref.harvestEndDate),
  };
}

function resolvePickerValue(range: DateRange | undefined, fallback: Moment): [Moment, Moment] {
  return range?.[0] && range?.[1] ? [range[0], range[1]] : [fallback, fallback];
}

export const MissingCropGrowingPeriod = ({
  recommendedSeedingDate,
  recommendedHarvestDate,
  existingCropsForValidationByField,
  selectedCropsWithFieldIds,
  disabled,
}: MissingCropGrowingPeriodProps) => {
  const { t } = useTranslation(["allFieldView", "historicalPracticeDataTableView"]);
  const form = Form.useFormInstance();
  const { validatePeriods } = useValidateCropPeriods();
  const [cropPeriodValidationErrors, setCropPeriodValidationErrors] = useState<CropFormValidationError[]>([]);

  const seedingDateRange = Form.useWatch(["seedingDateRange"], form) as DateRange | undefined;
  const harvestDateRange = Form.useWatch(["harvestDateRange"], form) as DateRange | undefined;

  const { hasMixedDates, initialSeedingRange, initialHarvestRange } = useMemo(
    () => computeInitialDateRanges(selectedCropsWithFieldIds.map(({ crop }) => crop)),
    [selectedCropsWithFieldIds]
  );

  // callers memoise this array: a fresh identity on every render would re-seed the inputs and wipe what the farmer is entering
  useEffect(() => {
    form.setFieldsValue({
      seedingDateRange: initialSeedingRange,
      harvestDateRange: initialHarvestRange,
    });
  }, [selectedCropsWithFieldIds]); // eslint-disable-line react-hooks/exhaustive-deps -- only set initial values when the selection changes, to avoid overwriting user input

  const seedingPickerValue = resolvePickerValue(seedingDateRange, recommendedSeedingDate);
  const harvestPickerValue = resolvePickerValue(harvestDateRange, recommendedHarvestDate);

  const runCropPeriodValidation = () => {
    const seedingRange = form.getFieldValue("seedingDateRange") as DateRange | undefined;
    const harvestRange = form.getFieldValue("harvestDateRange") as DateRange | undefined;
    if (!seedingRange?.[0] || !seedingRange?.[1] || !harvestRange?.[0] || !harvestRange?.[1]) {
      setCropPeriodValidationErrors([]);
      return [];
    }
    const seedingStart = seedingRange[0].format("YYYY-MM-DD");
    const seedingEnd = seedingRange[1].format("YYYY-MM-DD");
    const harvestStart = harvestRange[0].format("YYYY-MM-DD");
    const harvestEnd = harvestRange[1].format("YYYY-MM-DD");
    const allErrors = selectedCropsWithFieldIds.flatMap(({ crop, fieldId }) => {
      const currentCrop: CropFCPO = {
        ...crop,
        seedingStartDate: seedingStart,
        seedingEndDate: seedingEnd,
        harvestStartDate: harvestStart,
        harvestEndDate: harvestEnd,
      };
      return validatePeriods(currentCrop, existingCropsForValidationByField[fieldId] ?? []);
    });
    setCropPeriodValidationErrors(allErrors);
    return allErrors;
  };

  const hasValidationError = cropPeriodValidationErrors.length > 0;
  const firstError = cropPeriodValidationErrors[0];

  return (
    <div data-testid="missing-crop-growing-period-form" className="margin-top-200">
      <Form.Item
        name="cropPeriodValidation"
        hidden
        validateTrigger="onSubmit"
        rules={[
          {
            validator: () => {
              const errors = runCropPeriodValidation();
              if (errors.length > 0) {
                return Promise.reject(new Error(errors[0].message));
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        <input type="hidden" />
      </Form.Item>
      <Flex gap="1rem" className="margin-top-100 margin-bottom-100">
        <Flex vertical gap="1rem" className="date-picker-container" data-testid="seeding-date-range-picker-container">
          <RangePicker
            data-field-id="seedingDateRange"
            dataTestId="missing-crop-growing-period-seeding-date-range"
            initialValue={initialSeedingRange}
            defaultPickerValue={seedingPickerValue}
            format={["DD.MM.YYYY"]}
            label={t("allFieldView:cropForm.seedingDateRange")}
            name={["seedingDateRange"]}
            rules={[
              {
                // both ends are required: the save sends seedingStart and seedingEnd together, and a range the farmer
                // never touched arrives here as undefined rather than [null, null] — which used to pass and then save
                // nothing at all
                validator: (_, value: [Moment | null, Moment | null] | null) => {
                  if (value?.[0] && value?.[1]) return Promise.resolve();
                  return Promise.reject(new Error(t("allFieldView:cropForm.errorMessages.seedingDateRequired")));
                },
              },
            ]}
            value={seedingDateRange}
            status={cropPeriodValidationErrors.some((e) => e.fieldId === "seedingStartDate") ? "error" : undefined}
            help={hasMixedDates && cropPeriodValidationErrors.length === 0 ? t("allFieldView:cropForm.mixedValues") : undefined}
            disabled={disabled}
          />
        </Flex>
        <Flex vertical gap="1rem" className="date-picker-container" data-testid="harvest-date-range-picker-container">
          <RangePicker
            data-field-id="harvestDateRange"
            dataTestId="missing-crop-growing-period-harvest-date-range"
            format={["DD.MM.YYYY"]}
            initialValue={initialHarvestRange}
            defaultPickerValue={harvestPickerValue}
            name={["harvestDateRange"]}
            label={t("allFieldView:cropForm.harvestDateRange")}
            rules={[
              {
                validator: (_, value: [Moment | null, Moment | null] | null) => {
                  if (value?.[0] && value?.[1]) return Promise.resolve();
                  return Promise.reject(new Error(t("allFieldView:cropForm.errorMessages.harvestDateRequired")));
                },
              },
            ]}
            value={harvestDateRange}
            status={cropPeriodValidationErrors.some((e) => e.fieldId === "harvestEndDate") ? "error" : undefined}
            help={hasMixedDates && cropPeriodValidationErrors.length === 0 ? t("allFieldView:cropForm.mixedValues") : undefined}
            disabled={disabled}
          />
        </Flex>
      </Flex>
      {hasValidationError && firstError && (
        <AlertBanner
          type="error"
          iconName="tabler/alert-triangle"
          headingSize="h6"
          descriptionSize="paragraph-m"
          iconAlign="start"
          iconSize={24}
          gap={0}
          title={
            firstError.overlappingCrop
              ? t("historicalPracticeDataTableView:drawer.overlappingCropsTitle")
              : t("historicalPracticeDataTableView:drawer.faultyGrowingPeriodTitle")
          }
          description={t(firstError.overlappingCrop ? "historicalPracticeDataTableView:drawer.overlappingCropsMessage" : firstError.message, {
            cropName: firstError.overlappingCrop?.name,
            seedingStartDate: moment(firstError.overlappingCrop?.seedingStartDate).format("DD.MM.YYYY"),
            harvestEndDate: moment(firstError.overlappingCrop?.harvestEndDate).format("DD.MM.YYYY"),
          })}
          className="margin-top-100"
        />
      )}
    </div>
  );
};
